import React from 'react';
import { Box, Card, CardMedia, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import noImage from '../../assets/no-image.png';
import { v2Colors } from '../../design/theme';
import { paths } from '../../config/paths';
import { getProductUrlSlug } from '../../utils/productUrl';

const DISPLAY_FONT = '"Foregen", "Roboto Slab", Georgia, serif';

function lowestVisibleVariant(product) {
  return (product?.variants ?? [])
    .filter((v) => v.visibleToCustomer !== false && v.isActive !== false)
    .sort((a, b) => (Number(a.price) || 0) - (Number(b.price) || 0))[0];
}

function badgeStyle(badge) {
  const b = String(badge || '').toLowerCase();
  if (b === 'new') return { bgcolor: '#E8A317', color: '#1A130F' };
  if (b === 'bestseller') return { bgcolor: '#F5D9D5', color: v2Colors.primary };
  return { bgcolor: v2Colors.tagFill, color: v2Colors.tagText };
}

export default function MostLovedProductCard({ product }) {
  const navigate = useNavigate();
  const productSlug = getProductUrlSlug(product);
  const variant = lowestVisibleVariant(product);
  const img = product?.image || product?.images?.[0] || noImage;
  const subtitle = product?.subCategory || product?.aka || '';
  const badge = product?.badge || product?.productBadge;

  const goToProduct = () => {
    if (productSlug) navigate(paths.product(productSlug));
  };

  return (
    <Card
      elevation={0}
      sx={{
        width: 168,
        flexShrink: 0,
        borderRadius: 2.5,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: v2Colors.borderMuted,
        bgcolor: v2Colors.white,
      }}
    >
      <Box
        onClick={goToProduct}
        sx={{
          position: 'relative',
          bgcolor: v2Colors.surface,
          minHeight: 120,
          cursor: 'pointer',
        }}
      >
        {badge ? (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              zIndex: 1,
              px: 1,
              py: 0.35,
              borderRadius: 999,
              fontSize: '0.65rem',
              fontWeight: 700,
              letterSpacing: '0.03em',
              textTransform: 'capitalize',
              ...badgeStyle(badge),
            }}
          >
            {badge}
          </Box>
        ) : null}
        <CardMedia
          component="img"
          image={img}
          alt={product?.name || 'Product'}
          sx={{
            width: '100%',
            height: 120,
            objectFit: 'contain'
          }}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = noImage;
          }}
        />
      </Box>

      <Box sx={{ px: 1.25, py: 1.25 }}>
        <Typography
          variant="subtitle2"
          fontWeight={700}
          noWrap
          onClick={goToProduct}
          sx={{ fontFamily: DISPLAY_FONT, cursor: 'pointer', lineHeight: 1.2 }}
        >
          {product?.name}
        </Typography>
        {subtitle ? (
          <Typography variant="caption" color="text.secondary" noWrap display="block" sx={{ mt: 0.25 }}>
            {subtitle}
          </Typography>
        ) : null}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1, gap: 0.5 }}>
          {variant ? (
            <Typography variant="body2" fontWeight={800}>
              ₹{variant.price}
              {variant.weight ? (
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                  {variant.weight}
                </Typography>
              ) : null}
            </Typography>
          ) : (
            <Box />
          )}
          <Box
            component="button"
            type="button"
            onClick={goToProduct}
            sx={{
              border: 'none',
              margin: 0,
              cursor: 'pointer',
              borderRadius: 999,
              px: 1.25,
              py: 0.5,
              fontSize: '0.75rem',
              fontWeight: 700,
              fontFamily: 'inherit',
              bgcolor: v2Colors.primary,
              color: v2Colors.white,
              whiteSpace: 'nowrap',
            }}
          >
            View
          </Box>
        </Box>
      </Box>
    </Card>
  );
}
